import type { ComponentSize } from '@zoryn/constants'
import type { CSSProperties } from 'vue'
import type { GridColumnInfo, GridColumnProps, GridOptions } from '../types'
import { isFilterableColumn } from '../filter/filter-utils'
import { resolveSpecialColumnWidth } from './grid-size-metrics'

export type GridColumnAlign = NonNullable<GridColumnInfo['align']>

/** checkbox / seq 列默认宽度（default 尺寸），不可通过列配置修改 */
export const GRID_SPECIAL_COLUMN_WIDTH = resolveSpecialColumnWidth()

export function isGridSpecialColumn(column: Pick<GridColumnInfo, 'type'>) {
  return column.type === 'checkbox' || column.type === 'seq'
}

export function applySpecialColumnWidths(
  columns: GridColumnInfo[],
  size?: ComponentSize | null
) {
  const width = resolveSpecialColumnWidth(size)
  for (const column of columns) {
    if (isGridSpecialColumn(column)) {
      column.width = width
      column.minWidth = width
    }
  }
}

export function resolveColumnWidth(column: GridColumnInfo): number {
  if (isGridSpecialColumn(column)) {
    return Number(column.width) || GRID_SPECIAL_COLUMN_WIDTH
  }
  const width = Number(column.width) || 150
  const headerMin = Number(column.headerMinWidth) || 0
  return headerMin > 0 ? Math.max(width, headerMin) : width
}

export function resolveColumnMinWidth(column: GridColumnInfo): number {
  if (isGridSpecialColumn(column)) {
    return (
      Number(column.minWidth) ||
      Number(column.width) ||
      GRID_SPECIAL_COLUMN_WIDTH
    )
  }
  const minWidth = Number(column.minWidth) || 80
  const headerMin = Number(column.headerMinWidth) || 0
  return headerMin > 0 ? Math.max(minWidth, headerMin) : minWidth
}

export function resolveColumnAlign(align?: GridColumnAlign): GridColumnAlign {
  return align ?? 'center'
}

export function getColumnAlignStyle(
  align?: GridColumnAlign
): Pick<CSSProperties, 'textAlign' | 'justifyContent'> {
  const resolved = resolveColumnAlign(align)
  return {
    textAlign: resolved,
    justifyContent:
      resolved === 'left'
        ? 'flex-start'
        : resolved === 'right'
          ? 'flex-end'
          : 'center',
  }
}

const EDIT_TYPES = new Set([
  'input',
  'number',
  'date',
  'datetime',
  'lov',
  'switch',
  'html',
  'table',
  'file',
  'files',
  'image',
])

export function resolveColumnEditable(
  type?: string,
  editable?: boolean
): boolean {
  if (!type || !EDIT_TYPES.has(type)) {
    return false
  }
  if (editable === false) {
    return false
  }
  return true
}

export function resolveGridEditable(
  props?: { editable?: boolean },
  options?: GridOptions
): boolean {
  if (props?.editable !== undefined) {
    return props.editable !== false
  }
  if (options?.editable !== undefined) {
    return options.editable !== false
  }
  return true
}

export function resolveGridDblClickEdit(
  props?: { editable?: boolean; dblClickEdit?: boolean },
  options?: GridOptions
): boolean {
  if (props?.dblClickEdit !== undefined) {
    return props.dblClickEdit !== false
  }
  if (options?.dblClickEdit !== undefined) {
    return options.dblClickEdit !== false
  }
  return resolveGridEditable(props, options)
}

export function isGridColumnEditable(
  column: GridColumnInfo,
  tableEditable = true
) {
  if (!tableEditable || !column.field) {
    return false
  }
  return column.editable
}

/** 未显式指定 type 时：lovType → lov，options → table，否则 base */
export function resolveGridColumnType(
  col: Pick<GridColumnProps, 'type' | 'lovType' | 'options'>
) {
  if (col.type) {
    return col.type
  }
  if (col.lovType) {
    return 'lov'
  }
  if (col.options) {
    return 'table'
  }
  return 'base'
}

export function buildColumnInfo(
  col: GridColumnProps,
  index: number
): GridColumnInfo | null {
  if (col.visible === false) {
    return null
  }
  const type = resolveGridColumnType(col)
  if (!col.field && type !== 'seq' && type !== 'checkbox') {
    return null
  }
  const field = col.field || type
  const isSpecial = isGridSpecialColumn({ type })
  const widthSpecified =
    !isSpecial && col.width != null && col.width !== '' && Number(col.width) > 0
  const { lovType, ...rest } = col
  return {
    ...rest,
    field,
    id: `col_${field}_${index}`,
    type,
    order: null,
    editable: resolveColumnEditable(type, col.editable),
    sortable: col.sortable ?? !isSpecial,
    widthSpecified,
    width: isSpecial
      ? GRID_SPECIAL_COLUMN_WIDTH
      : widthSpecified
        ? Number(col.width) || 150
        : undefined,
    minWidth: isSpecial ? GRID_SPECIAL_COLUMN_WIDTH : col.minWidth,
    align: resolveColumnAlign(col.align),
    ...(type === 'lov' && lovType ? { lovType } : {}),
  }
}

export function refreshVisibleColumns($grid: {
  internalData: {
    allColumns: GridColumnInfo[]
    visibleColumns: GridColumnInfo[]
    titleList: GridColumnInfo[]
  }
}) {
  const { internalData } = $grid
  internalData.visibleColumns = internalData.allColumns.filter(
    (col) => col.visible !== false
  )
  internalData.titleList =
    internalData.visibleColumns.filter(isFilterableColumn)
}

export function collectLovTypes(
  columns: GridColumnProps[] | undefined
): string[] {
  if (!columns?.length) {
    return []
  }
  const types = new Set<string>()
  for (const col of columns) {
    const type = resolveGridColumnType(col)
    if (type === 'lov' && col.lovType) {
      types.add(col.lovType)
    }
  }
  return [...types]
}
